import React from "react";
import {
  CLUB_TIMEZONE_LABEL,
  WEEKDAYS,
  describeSchedule,
  scheduleError,
} from "../../pages/AdminDashboard/emailSchedule";

const inputStyle =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold";
const labelStyle = "text-sm font-medium text-gray-700 font-manrope";

const UNIT_LABEL = { daily: "day(s)", weekly: "week(s)", monthly: "month(s)" };

/**
 * When a scheduled email goes: once, or every N days / weeks (on chosen
 * days) / months, ending never, on a date, or after N sends. Shared by the
 * composer and the edit dialog so both offer exactly the same choices.
 */
export default function ScheduleEditor({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const repeating = value.repeat !== "none";
  const error = scheduleError(value);
  const summary = describeSchedule(value);

  const toggleDay = (day) =>
    set({
      weekdays: value.weekdays.includes(day)
        ? value.weekdays.filter((d) => d !== day)
        : [...value.weekdays, day],
    });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label htmlFor="schedule-date" className={labelStyle}>
            Date
          </label>
          <input
            id="schedule-date"
            type="date"
            value={value.startDate}
            onChange={(e) => set({ startDate: e.target.value })}
            className={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="schedule-time" className={labelStyle}>
            Time
          </label>
          <input
            id="schedule-time"
            type="time"
            value={value.sendTime}
            onChange={(e) => set({ sendTime: e.target.value })}
            className={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="schedule-repeat" className={labelStyle}>
            Repeat
          </label>
          <select
            id="schedule-repeat"
            value={value.repeat}
            onChange={(e) => set({ repeat: e.target.value })}
            className={inputStyle}
          >
            <option value="none">Does not repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {repeating && (
        <>
          <div className="flex items-center gap-2">
            <label htmlFor="schedule-interval" className={labelStyle}>
              Every
            </label>
            <input
              id="schedule-interval"
              type="number"
              min={1}
              max={99}
              value={value.interval}
              onChange={(e) => set({ interval: e.target.value })}
              className={`${inputStyle} !w-20`}
            />
            <span className="text-sm font-manrope text-gray-700">
              {UNIT_LABEL[value.repeat]}
            </span>
          </div>

          {value.repeat === "weekly" && (
            <div>
              <span className={labelStyle}>On</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {WEEKDAYS.map((d) => {
                  const on = value.weekdays.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDay(d.value)}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-manrope ${
                        on
                          ? "bg-[#173151] text-white border-[#173151]"
                          : "bg-white text-gray-700 border-gray-300"
                      }`}
                    >
                      {d.short}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <fieldset>
            <legend className={labelStyle}>Ends</legend>
            <div className="flex flex-wrap items-center gap-4 mt-1 text-sm font-manrope">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="schedule-ends"
                  checked={value.ends === "never"}
                  onChange={() => set({ ends: "never" })}
                />
                Never
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="schedule-ends"
                  checked={value.ends === "on"}
                  onChange={() => set({ ends: "on" })}
                />
                On
                <input
                  type="date"
                  aria-label="End date"
                  value={value.endDate}
                  onChange={(e) => set({ ends: "on", endDate: e.target.value })}
                  className="px-2 py-1 border border-gray-300 rounded-lg"
                />
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="schedule-ends"
                  checked={value.ends === "after"}
                  onChange={() => set({ ends: "after" })}
                />
                After
                <input
                  type="number"
                  min={1}
                  aria-label="Number of sends"
                  value={value.maxRuns}
                  onChange={(e) =>
                    set({ ends: "after", maxRuns: e.target.value })
                  }
                  className="w-20 px-2 py-1 border border-gray-300 rounded-lg"
                />
                sends
              </label>
            </div>
          </fieldset>
        </>
      )}

      <p className="text-xs font-manrope text-text-muted">
        Times are {CLUB_TIMEZONE_LABEL}.
      </p>
      {error ? (
        <p className="text-sm font-manrope text-red-600" role="alert">
          {error}
        </p>
      ) : (
        <p className="text-sm font-manrope text-[#173151]">{summary}</p>
      )}
    </div>
  );
}
